import React, { useState } from 'react';
import { AlertTriangle, Clock, DollarSign, Check, Loader2, MapPin, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { roadsideAPI } from '../../services/roadsideAPI';

export const DisclaimerConfirm = ({ requestData, onNext, onBack }) => {
  const { t } = useTranslation();
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if after hours (8 AM - 6 PM)
  const isAfterHours = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour < 8 || hour >= 18;
  };

  const afterHours = isAfterHours();
  const callOutFee = afterHours ? 60.0 : 0.0;

  const handleSubmit = async () => {
    if (!disclaimerAccepted) {
      toast.error(t('roadside.errors.disclaimerRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const fullRequest = {
        customer_name: requestData.customerInfo?.name || 'Guest',
        customer_phone: requestData.customerInfo?.phone || '',
        customer_email: requestData.customerInfo?.email || null,
        location: requestData.location,
        service_type: requestData.service_type,
        notes: requestData.notes || '',
        vehicle_year: requestData.vehicle_year,
        vehicle_make: requestData.vehicle_make,
        vehicle_model: requestData.vehicle_model,
        tire_size: requestData.tire_size,
        disclaimer_accepted: true
      };

      // Create the request first
      const response = await roadsideAPI.createRequest(fullRequest);

      // Upload photos if any
      if (requestData.photos && requestData.photos.length > 0) {
        try {
          await roadsideAPI.uploadPhotos(requestData.photos, response.request_number);
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          // Don't fail the request if photos fail to upload
          toast.warning('Request submitted, but photos upload failed');
        }
      }

      toast.success(t('roadside.success.requestSubmitted'), {
        description: t('roadside.success.findingMechanic')
      });

      onNext(response);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(t('roadside.errors.submitFailed'), {
        description: error.response?.data?.detail || t('roadside.errors.submitFailedMsg')
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">{t('roadside.step4.title')}</h1>
          <p className="text-sm text-gray-600 mt-1">{t('roadside.step4.step')}: {t('roadside.step4.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Request Summary */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{t('roadside.step4.requestSummary')}</h2>

          <div className="space-y-4">
            {/* Vehicle Info */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('roadside.step4.vehicle')}</p>
                <p className="font-semibold text-gray-900">
                  {requestData.vehicle_year} {requestData.vehicle_make} {requestData.vehicle_model}
                </p>
                {requestData.tire_size && (
                  <p className="text-sm text-gray-600 mt-1">{t('roadside.step4.tireSize')} <span className="font-mono font-semibold tire-size">{requestData.tire_size}</span></p>
                )}
              </div>
            </div>

            {/* Service Type */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('roadside.step4.serviceType')}</p>
                <p className="font-semibold text-gray-900">{requestData.service_type}</p>
                {requestData.notes && (
                  <p className="text-sm text-gray-600 mt-1 italic">"{requestData.notes}"</p>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('roadside.step4.location')}</p>
                <p className="font-medium text-gray-900 text-sm">{requestData.location.address}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* After Hours Notice */}
        {afterHours && (
          <Card className="p-6 bg-yellow-50 border-2 border-yellow-300">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-yellow-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-yellow-800" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-yellow-900 mb-1">{t('roadside.step4.afterHours')}</h3>
                <p className="text-sm text-yellow-800 mb-3">
                  {t('roadside.step4.afterHoursMsg')}
                </p>
                <div className="flex items-center gap-2 bg-white rounded-lg p-3 border border-yellow-300">
                  <DollarSign className="w-5 h-5 text-yellow-800" />
                  <div>
                    <p className="font-bold text-yellow-900">{t('roadside.step4.callOutFee')} <span className="numeric">${callOutFee.toFixed(2)}</span></p>
                    <p className="text-xs text-yellow-800">{t('roadside.step4.afterHoursApplies')}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Disclaimer */}
        <Card className="p-6 border-2 border-gray-300">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-gray-700 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">{t('roadside.step4.importantNotice')}</h3>
              <div className="text-sm text-gray-700 space-y-2">
                <p>{t('roadside.step4.disclaimer1')}</p>
                <p>{t('roadside.step4.disclaimer2')}</p>
                <p>{t('roadside.step4.disclaimer3')}</p>
              </div>
            </div>
          </div>

          {/* Acceptance Checkbox */}
          <div className="flex items-start gap-3 mt-6 p-4 bg-gray-50 rounded-lg">
            <Checkbox
              id="disclaimer"
              checked={disclaimerAccepted}
              onCheckedChange={setDisclaimerAccepted}
              className="mt-1"
            />
            <Label htmlFor="disclaimer" className="text-sm text-gray-900 cursor-pointer flex-1">
              {afterHours 
                ? t('roadside.step4.acceptTerms', { fee: callOutFee.toFixed(2) })
                : t('roadside.step4.acceptTermsNoFee')
              }
            </Label>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button onClick={onBack} variant="outline" size="lg" className="flex-1 border-2 py-6" disabled={isSubmitting}>
            {t('common.back')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!disclaimerAccepted || isSubmitting}
            size="lg"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0 animate-spin" />
                {t('roadside.step4.submitting')}
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('roadside.step4.confirmSubmit')}
              </>
            )}
          </Button>
        </div>

        {/* Safety Info */}
        <div className="text-center text-xs text-gray-500 px-4">
          <p>{t('roadside.step4.safetyNote')}</p>
        </div>
      </div>
    </div>
  );
};
